<template>
  <div class="relative w-full">
    <div class="flex justify-between items-center w-full px-2">
      <span class="text-xs font-semibold">Patient</span>
      <div class="flex gap-1">
        <!-- Sort button -->
        <button
          @click="toggleSortMenu"
          class="px-1.5 py-0.5 rounded text-xs transition-all duration-200 hover:bg-gray-100"
          :class="(showSortMenu || currentSort) ? 'bg-primary-500 text-white hover:bg-primary-500' : ''"
          title="Sort patient data"
        >
          <span v-if="!currentSort">⇅</span>
          <span v-else>{{ currentSort.direction === 'asc' ? '↑' : '↓' }}</span>
        </button>

        <!-- Filter button -->
        <button
          @click="toggleFilterMenu"
          class="px-1.5 py-0.5 rounded text-xs transition-all duration-200 hover:bg-gray-100"
          :class="(showFilterMenu || hasActiveFilters) ? 'bg-primary-500 text-white hover:bg-primary-500' : ''"
          title="Filter patient data"
        >
          🔍
        </button>
      </div>
    </div>

    <!-- Teleport dropdowns to body -->
    <Teleport to="body">
      <!-- Sort dropdown -->
      <div
        v-if="showSortMenu"
        class="fixed bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden animate-fadeIn"
        :style="dropdownStyle"
        ref="sortMenu"
      >
        <div class="px-3 py-2 text-xs font-semibold text-gray-600 uppercase bg-gray-50 border-b border-gray-200">
          Sort by:
        </div>
        <div class="py-1">
          <button
            v-for="option in sortOptions"
            :key="option.field"
            @click="applySort(option)"
            class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors duration-150 flex justify-between items-center"
            :class="currentSort?.field === option.field ? 'bg-blue-50 text-blue-700' : ''"
          >
            {{ option.label }}
            <span v-if="currentSort?.field === option.field" class="text-xs ml-2">
              {{ currentSort.direction === 'asc' ? '↑' : '↓' }}
            </span>
          </button>
        </div>
        <div class="border-t border-gray-200">
          <button
            @click="clearSort"
            class="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors duration-150"
          >
            Clear Sort
          </button>
        </div>
      </div>

      <!-- Filter dropdown - Simplified -->
      <div
        v-if="showFilterMenu"
        class="fixed bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden animate-fadeIn"
        :style="dropdownStyle"
        ref="filterMenu"
      >
        <div class="px-3 py-2 text-xs font-semibold text-gray-600 uppercase bg-gray-50 border-b border-gray-200">
          Filter by:
        </div>

        <div class="p-3 space-y-2">
          <input
            v-model="filters.patientId"
            @input="applyFilters"
            placeholder="Patient ID"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent"
          >
          <input
            v-model="filters.lastName"
            @input="applyFilters"
            placeholder="Last Name"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent"
          >
          <input
            v-model="filters.firstName"
            @input="applyFilters"
            placeholder="First Name"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent"
          >
          <input
            v-model="filters.pcp"
            @input="applyFilters"
            placeholder="PCP"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent"
          >
        </div>

        <div class="border-t border-gray-200">
          <button
            @click="clearFilters"
            class="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors duration-150"
          >
            Clear All Filters
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
export default {
  name: 'PatientHeaderComponent',
  props: ['params'],
  data() {
    return {
      showSortMenu: false,
      showFilterMenu: false,
      currentSort: null,
      filters: {
        patientId: '',
        lastName: '',
        firstName: '',
        pcp: ''
      },
      sortOptions: [
        { field: 'task.patient_identifier', label: 'Patient ID' },
        { field: 'task.bene_last_name', label: 'Last Name' },
        { field: 'task.bene_first_name', label: 'First Name' },
        { field: 'task.bene_dob', label: 'Date of Birth' },
        { field: 'task.bene_pcp', label: 'PCP' }
      ],
      dropdownStyle: {
        position: 'fixed',
        top: '0px',
        left: '0px',
        zIndex: 9999,
        minWidth: '250px',
        maxWidth: '350px'
      }
    };
  },
  computed: {
    hasActiveFilters() {
      return Object.values(this.filters).some(v => v);
    }
  },
  methods: {
    updateDropdownPosition() {
      const headerEl = this.$el.querySelector('.flex');
      if (!headerEl) return;

      const rect = headerEl.getBoundingClientRect();

      this.dropdownStyle = {
        position: 'fixed',
        top: `${rect.bottom + 4}px`,
        left: `${rect.left}px`,
        zIndex: 9999,
        minWidth: '250px',
        maxWidth: '350px'
      };
    },

    toggleSortMenu() {
      this.showSortMenu = !this.showSortMenu;
      this.showFilterMenu = false;
      if (this.showSortMenu) {
        this.$nextTick(() => {
          this.updateDropdownPosition();
        });
      }
    },

    toggleFilterMenu() {
      this.showFilterMenu = !this.showFilterMenu;
      this.showSortMenu = false;
      if (this.showFilterMenu) {
        this.$nextTick(() => {
          this.updateDropdownPosition();
        });
      }
    },

    applySort(option) {
      const newDirection =
        this.currentSort?.field === option.field && this.currentSort?.direction === 'asc'
          ? 'desc'
          : 'asc';

      this.currentSort = {
        field: option.field,
        direction: newDirection
      };

      this.params.context.onPatientSort({
        field: option.field,
        direction: newDirection
      });

      this.showSortMenu = false;
    },

    clearSort() {
      this.currentSort = null;
      this.params.context.onPatientSort(null);
      this.showSortMenu = false;
    },

    applyFilters() {
      this.params.context.onPatientFilter({
        patientId: this.filters.patientId,
        lastName: this.filters.lastName,
        firstName: this.filters.firstName,
        pcp: this.filters.pcp
      });
    },

    clearFilters() {
      this.filters = {
        patientId: '',
        lastName: '',
        firstName: '',
        pcp: ''
      };

      this.params.context.onPatientClearFilters();
      this.showFilterMenu = false;
    }
  },
  mounted() {
    const handleClickOutside = (e) => {
      const sortMenu = this.$refs.sortMenu;
      const filterMenu = this.$refs.filterMenu;
      const header = this.$el;

      if (!header.contains(e.target) &&
          (!sortMenu || !sortMenu.contains(e.target)) &&
          (!filterMenu || !filterMenu.contains(e.target))) {
        this.showSortMenu = false;
        this.showFilterMenu = false;
      }
    };

    document.addEventListener('click', handleClickOutside);

    const handleScroll = () => {
      if (this.showSortMenu || this.showFilterMenu) {
        this.updateDropdownPosition();
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);

    this.cleanupFunctions = [
      () => document.removeEventListener('click', handleClickOutside),
      () => window.removeEventListener('scroll', handleScroll, true),
      () => window.removeEventListener('resize', handleScroll)
    ];
  },

  beforeUnmount() {
    if (this.cleanupFunctions) {
      this.cleanupFunctions.forEach(fn => fn());
    }
  }
};
</script>

<style>
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.2s ease-out;
}
</style>
